import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreatePersoonInput,
  Persoon,
  PersoonDetail,
  PersoonRow,
  UpdatePersoonInput,
} from '@fleet/shared';
import { api } from '../../lib/apiClient';

/** Invalidate everything that a link/unlink can shift. */
function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ['personen', 'passen', 'dashboard', 'cars']) {
    qc.invalidateQueries({ queryKey: [key] });
  }
}

export function usePersonen() {
  return useQuery({
    queryKey: ['personen'],
    queryFn: () => api.get<{ personen: PersoonRow[] }>('/personen').then((r) => r.personen),
  });
}

/** Only archived (uit dienst) people, for the personen-archief page. */
export function useArchivedPersonen() {
  return useQuery({
    queryKey: ['personen', 'archief'],
    queryFn: () =>
      api
        .get<{ personen: PersoonRow[] }>('/personen?includeArchived=true')
        .then((r) => r.personen.filter((p) => !p.actief)),
  });
}

export function usePersoonDetail(id: string) {
  return useQuery({
    queryKey: ['personen', id],
    queryFn: () => api.get<PersoonDetail>(`/personen/${encodeURIComponent(id)}`),
  });
}

export function useCreatePersoon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePersoonInput) =>
      api.post<{ persoon: Persoon }>('/personen', input).then((r) => r.persoon),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdatePersoon(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdatePersoonInput) =>
      api
        .patch<{ persoon: Persoon }>(`/personen/${encodeURIComponent(id)}`, patch)
        .then((r) => r.persoon),
    onSuccess: () => {
      invalidateAll(qc);
      qc.invalidateQueries({ queryKey: ['personen', id] });
    },
  });
}

export function useArchiveerPersoon(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ persoon: Persoon }>(`/personen/${encodeURIComponent(id)}/archiveer`),
    onSuccess: () => {
      invalidateAll(qc);
      qc.invalidateQueries({ queryKey: ['personen', id] });
    },
  });
}

export function useDearchiveerPersoon(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ persoon: Persoon }>(`/personen/${encodeURIComponent(id)}/dearchiveer`),
    onSuccess: () => {
      invalidateAll(qc);
      qc.invalidateQueries({ queryKey: ['personen', id] });
    },
  });
}

export function useUpdateKoppeling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; van?: string; tot?: string | null }) =>
      api.patch(`/personen/koppelingen/${encodeURIComponent(v.id)}`, {
        ...(v.van !== undefined ? { van: v.van } : {}),
        ...(v.tot !== undefined ? { tot: v.tot } : {}),
      }),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteKoppeling() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del(`/personen/koppelingen/${encodeURIComponent(id)}`),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useLinkAuto(persoonId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { kenteken: string; van?: string }) =>
      api.post(`/personen/${encodeURIComponent(persoonId)}/autos`, v),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUnlinkAuto(persoonId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { kenteken: string; tot?: string }) =>
      api.post(
        `/personen/${encodeURIComponent(persoonId)}/autos/${encodeURIComponent(v.kenteken)}/ontkoppel`,
        v.tot ? { tot: v.tot } : {},
      ),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useLinkPas(persoonId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { cardId: string; van?: string }) =>
      api.post(`/personen/${encodeURIComponent(persoonId)}/passen`, v),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUnlinkPas(persoonId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { cardId: string; tot?: string }) =>
      api.post(
        `/personen/${encodeURIComponent(persoonId)}/passen/${encodeURIComponent(v.cardId)}/ontkoppel`,
        v.tot ? { tot: v.tot } : {},
      ),
    onSuccess: () => invalidateAll(qc),
  });
}
